import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Check, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Icon from 'shared/components/Icon';
import FloatingMenu from 'shared/components/ui/FloatingMenu';
import { addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, setMonth, setYear, getYear, getMonth } from 'date-fns';
import { primary } from 'shared/theme/trodoColors';

const EventsTimeFilter = ({
  onTimeFilterChange,
  selectedPreset = 'today',
  openCustomOnMount = false,
  hidePresetButtons = false,
  onClose = null,
  // 'left' | 'right' — which edge the dropdowns hang from. Pass 'right' when
  // the control sits at the right end of a toolbar, or the 750px custom panel
  // walks off the viewport.
  menuAlign = 'left',
}) => {
  const [isCustomOpen, setIsCustomOpen] = useState(openCustomOnMount);
  const [customMode, setCustomMode] = useState('fixed'); // 'fixed', 'last', 'since'
  const [enableTimeRanges, setEnableTimeRanges] = useState(false);
  const [activePreset, setActivePreset] = useState(selectedPreset);
  
  // Fixed mode state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  
  // Last mode state
  const [lastAmount, setLastAmount] = useState(1);
  const [lastUnit, setLastUnit] = useState('days');
  const [endingAmount, setEndingAmount] = useState(0);
  const [endingUnit, setEndingUnit] = useState('days');
  
  // Since mode state
  const [sinceDate, setSinceDate] = useState('');
  const [sinceTime, setSinceTime] = useState('00:00');

  // Calendar UI state (for fixed/since modes) - show 2 months side by side
  const [calendarMonth1, setCalendarMonth1] = useState(() => startOfMonth(new Date()));
  const [calendarMonth2, setCalendarMonth2] = useState(() => startOfMonth(addMonths(new Date(), 1)));
  
  const dropdownRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const closePresetMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const presets = [
    { label: 'Custom', value: 'custom' },
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: '24H', value: '24h' },
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '3M', value: '3m' },
    { label: '6M', value: '6m' },
    { label: '12M', value: '12m' }
  ];

  const timeUnits = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'quarters', label: 'Quarters' }
  ];

  const MODE_ACTIVE_COLOR = primary;

  const parseYmd = (ymd) => {
    if (!ymd) return null;
    // ymd is YYYY-MM-DD; this parses in local time reliably for our UI
    const d = new Date(ymd);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toYmd = (d) => {
    if (!d) return '';
    try {
      return format(d, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  };

  // Generate month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: format(new Date(2024, i, 1), 'MMMM')
    }));
  }, []);

  // Generate year options (current year ± 10 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  }, []);

  const CalendarGrid = ({ mode, month, onMonthChange }) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstWeekday = monthStart.getDay(); // 0=Sun

    const startSelected = mode === 'fixed' ? parseYmd(startDate) : parseYmd(sinceDate);
    const endSelected = mode === 'fixed' ? parseYmd(endDate) : null;

    const isInRange = (day) => {
      if (mode !== 'fixed') return false;
      if (!startSelected || !endSelected) return false;
      const a = startSelected <= endSelected ? startSelected : endSelected;
      const b = startSelected <= endSelected ? endSelected : startSelected;
      return day >= a && day <= b;
    };

    const handleDayClick = (day) => {
      const ymd = toYmd(day);
      if (!ymd) return;

      if (mode === 'since') {
        setSinceDate(ymd);
        return;
      }

      // fixed range selection
      if (!startDate || (startDate && endDate)) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      // start selected, pick end
      const start = parseYmd(startDate);
      if (!start) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      // If user clicks a date before start, treat it as new start.
      if (day < start) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      setEndDate(ymd);
    };

    const weekdayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    // Convert JS Sun=0..Sat=6 into Monday-first layout
    const leadingEmpty = (firstWeekday + 6) % 7;

    const handleMonthChange = (newMonth) => {
      const newDate = setMonth(month, newMonth);
      onMonthChange(newDate);
    };

    const handleYearChange = (newYear) => {
      const newDate = setYear(month, newYear);
      onMonthChange(newDate);
    };

    return (
      <div className="select-none">
        <div className="flex items-center justify-between mb-1.5">
          <button
            type="button"
            onClick={() => onMonthChange(subMonths(month, 1))}
            className="p-1 rounded-lg hover:bg-surface-hover text-[color:var(--text-muted)]"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <select
              value={getMonth(month)}
              onChange={(e) => handleMonthChange(parseInt(e.target.value))}
              className="text-sm font-medium text-[color:var(--text-primary)] bg-transparent border-none outline-none cursor-pointer"
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={getYear(month)}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className="text-sm font-medium text-[color:var(--text-primary)] bg-transparent border-none outline-none cursor-pointer"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="p-1 rounded-lg hover:bg-surface-hover text-[color:var(--text-muted)]"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 text-xs text-[color:var(--text-muted)] mb-1">
          {weekdayLabels.map((w) => (
            <div key={w} className="text-center py-0.5">{w}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: leadingEmpty }).map((_, idx) => (
            <div key={`empty-${idx}`} className="h-7" />
          ))}

          {days.map((day) => {
            const isStart = mode === 'fixed' && startSelected && isSameDay(day, startSelected);
            const isEnd = mode === 'fixed' && endSelected && isSameDay(day, endSelected);
            const isSingle = mode === 'since' && startSelected && isSameDay(day, startSelected);
            const inRange = isInRange(day);

            const base =
              'h-7 w-7 flex items-center justify-center text-sm rounded-lg transition-colors';
            const selected =
              isStart || isEnd || isSingle ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-sunken)]';
            const rangeBg = inRange && !(isStart || isEnd) ? 'bg-[color:var(--surface-sunken)]' : '';

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDayClick(day)}
                className={`${base} ${rangeBg} ${selected}`}
                aria-label={format(day, 'yyyy-MM-dd')}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Sync second month when first month changes (always one month ahead)
  useEffect(() => {
    const expectedSecond = startOfMonth(addMonths(calendarMonth1, 1));
    if (expectedSecond.getTime() !== calendarMonth2.getTime()) {
      setCalendarMonth2(expectedSecond);
    }
  }, [calendarMonth1]);

  // Sync first month when second month changes (always one month behind)
  // Only sync if second month is not already one month ahead of first
  useEffect(() => {
    const expectedFirst = startOfMonth(subMonths(calendarMonth2, 1));
    if (expectedFirst.getTime() !== calendarMonth1.getTime()) {
      setCalendarMonth1(expectedFirst);
    }
  }, [calendarMonth2]);

  // The custom panel is a portaled FloatingMenu: it reports outside clicks / Escape here.
  const closeCustomPanel = useCallback(() => {
    setIsCustomOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  // Keep active preset in sync with parent state
  useEffect(() => {
    setActivePreset(selectedPreset);
  }, [selectedPreset]);

  // Calculate date range for presets
  const getPresetDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return {
          from: new Date(today.getTime()),
          to: new Date(now.getTime())
        };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return {
          from: yesterday,
          to: yesterdayEnd
        };
      case '24h':
        return {
          from: new Date(now.getTime() - 24 * 60 * 60 * 1000),
          to: now
        };
      case '7d':
        return {
          from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '30d':
        return {
          from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '3m':
        return {
          from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '6m':
        return {
          from: new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '12m':
        return {
          from: new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000),
          to: now
        };
      default:
        return null;
    }
  };

  // Convert date and time to ISO string
  const combineDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(':');
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date;
  };

  // Apply preset
  const handlePresetSelect = (preset) => {
    if (preset === 'custom') {
      setIsCustomOpen(true);
      setActivePreset('custom');
      return;
    }

    setIsCustomOpen(false);
    setActivePreset(preset);
    const range = getPresetDateRange(preset);
    if (range) {
      onTimeFilterChange({
        type: 'preset',
        preset,
        from: range.from.toISOString(),
        to: range.to.toISOString()
      });
    }
  };

  // Apply custom filter
  const handleCustomApply = () => {
    if (customMode === 'fixed') {
      if (!startDate || !endDate) return;
      
      let fromDate, toDate;
      
      if (enableTimeRanges) {
        fromDate = combineDateTime(startDate, startTime);
        toDate = combineDateTime(endDate, endTime);
      } else {
        // Default: full day inclusion
        fromDate = combineDateTime(startDate, '00:00');
        toDate = combineDateTime(endDate, '23:59');
      }
      
      if (fromDate && toDate) {
        setActivePreset('custom');
        onTimeFilterChange({
          type: 'custom',
          mode: 'fixed',
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          enableTimeRanges
        });
        setIsCustomOpen(false);
        if (onClose) onClose();
      }
    } else if (customMode === 'last') {
      // Calculate "last X units ending Y units ago"
      const now = new Date();
      const endingOffset = calculateOffset(endingAmount, endingUnit);
      const windowSize = calculateOffset(lastAmount, lastUnit);
      
      const endDate = new Date(now.getTime() - endingOffset);
      const startDate = new Date(endDate.getTime() - windowSize);
      
      setActivePreset('custom');
      onTimeFilterChange({
        type: 'custom',
        mode: 'last',
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        lastAmount,
        lastUnit,
        endingAmount,
        endingUnit
      });
      setIsCustomOpen(false);
      if (onClose) onClose();
    } else if (customMode === 'since') {
      if (!sinceDate) return;
      
      const now = new Date();
      let fromDate;
      
      if (enableTimeRanges) {
        fromDate = combineDateTime(sinceDate, sinceTime);
      } else {
        fromDate = combineDateTime(sinceDate, '00:00');
      }
      
      // End is always today at 23:59 (or now if time ranges enabled)
      const toDate = enableTimeRanges ? now : combineDateTime(
        now.toISOString().split('T')[0],
        '23:59'
      );
      
      if (fromDate) {
        setActivePreset('custom');
        onTimeFilterChange({
          type: 'custom',
          mode: 'since',
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          enableTimeRanges
        });
        setIsCustomOpen(false);
        if (onClose) onClose();
      }
    }
  };

  // Calculate offset in milliseconds
  const calculateOffset = (amount, unit) => {
    const multipliers = {
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
      weeks: 7 * 24 * 60 * 60 * 1000,
      months: 30 * 24 * 60 * 60 * 1000,
      quarters: 90 * 24 * 60 * 60 * 1000
    };
    return amount * (multipliers[unit] || multipliers.days);
  };

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    return date.toISOString().split('T')[0];
  };

  const getPresetLabel = () => {
    const preset = presets.find(p => p.value === selectedPreset);
    return preset ? preset.label : 'Custom';
  };

  const showCustomPanel = isCustomOpen || (hidePresetButtons && openCustomOnMount);

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      {/* Stepper-style preset selector: ‹  label  ›
          - single click on chevrons → step prev/next preset
          - double-click on label   → open dropdown of all presets
          - clicking label once     → also opens the dropdown
       */}
      {!hidePresetButtons && (() => {
        // Order presets so ←/→ stepping reads chronologically (Today → 7D → 30D…),
        // and Custom always sits at the end as the explicit "anything else".
        const STEP_ORDER = ['today', 'yesterday', '24h', '7d', '30d', '3m', '6m', '12m', 'custom'];
        const stepPresets = STEP_ORDER
          .map((v) => presets.find((p) => p.value === v))
          .filter(Boolean);
        const currentIdx = Math.max(0, stepPresets.findIndex((p) => p.value === activePreset));
        const goPrev = () => {
          if (currentIdx <= 0) return;
          handlePresetSelect(stepPresets[currentIdx - 1].value);
        };
        const goNext = () => {
          if (currentIdx >= stepPresets.length - 1) return;
          handlePresetSelect(stepPresets[currentIdx + 1].value);
        };
        const currentPreset = stepPresets[currentIdx] || stepPresets[0];
        const hasPrev = currentIdx > 0;
        const hasNext = currentIdx < stepPresets.length - 1;

        // Human-friendly label: range-ending-now reads "X – Now";
        // Today / Yesterday keep their short names; Custom uses its own panel state.
        const fmt = (d) => format(d, 'd MMM');
        const isSameDate = (a, b) => isSameDay(a, new Date());
        let currentLabel;
        if (currentPreset.value === 'today') {
          currentLabel = 'Today';
        } else if (currentPreset.value === 'yesterday') {
          currentLabel = 'Yesterday';
        } else if (currentPreset.value === 'custom') {
          // Show the actual fixed window if we have one, otherwise just "Custom".
          if (startDate && endDate) {
            const f = parseYmd(startDate);
            const t = parseYmd(endDate);
            currentLabel = f && t ? `${fmt(f)} – ${fmt(t)}` : 'Custom';
          } else if (sinceDate) {
            const f = parseYmd(sinceDate);
            currentLabel = f ? `${fmt(f)} – Now` : 'Custom';
          } else {
            currentLabel = 'Custom';
          }
        } else {
          const range = getPresetDateRange(currentPreset.value);
          currentLabel = range ? `${fmt(range.from)} – Now` : currentPreset.label;
        }

        return (
          <div className="relative inline-flex h-8 items-stretch" ref={mobileMenuRef}>
            {/* Outer pill */}
            <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
              {/* Prev */}
              <button
                type="button"
                onClick={goPrev}
                disabled={!hasPrev}
                title="Previous range"
                className="flex w-7 items-center justify-center text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous range"
              >
                <ChevronLeft size={14} />
              </button>
              {/* Label / opener */}
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((v) => !v)}
                onDoubleClick={() => setIsMobileMenuOpen(true)}
                title="Click to change range"
                className="flex min-w-[140px] items-center justify-center gap-1.5 border-x border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
                style={{ fontFamily: 'Inter, sans-serif' }}
                aria-haspopup="listbox"
                aria-expanded={isMobileMenuOpen}
              >
                <Icon name="calendar" size={13} color="var(--text-muted)" />
                <span className="whitespace-nowrap">{currentLabel}</span>
              </button>
              {/* Next */}
              <button
                type="button"
                onClick={goNext}
                disabled={!hasNext}
                title="Next range"
                className="flex w-7 items-center justify-center text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next range"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            {/* Dropdown of every option: portaled, kept in the viewport, scrolls itself */}
            <FloatingMenu anchorRef={mobileMenuRef} open={isMobileMenuOpen} onClose={closePresetMenu} width={160} align={menuAlign}
              maxHeight={320} role="listbox" aria-label="Time range">
              {stepPresets.map((preset) => {
                const active = activePreset === preset.value;
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => {
                      handlePresetSelect(preset.value);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex h-8 w-full items-center gap-2 px-3 text-left text-[12px] transition-colors ${
                      active
                        ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium'
                        : 'text-[color:var(--text-primary)] hover:bg-surface-hover'
                    }`}
                    role="option"
                    aria-selected={active}
                  >
                    {preset.value === 'custom' && (
                      <Icon name="calendar" size={12} color="var(--text-muted)" />
                    )}
                    <span className="flex-1">{preset.label}</span>
                    {active && <Check className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-primary)]" aria-hidden />}
                  </button>
                );
              })}
            </FloatingMenu>
          </div>
        );
      })()}

      {/* Custom time filter popup */}
      <FloatingMenu anchorRef={dropdownRef} open={showCustomPanel} onClose={closeCustomPanel} width={750} align={menuAlign}
        maxHeight={600} minHeight={440} role="dialog" aria-label="Custom time range">
          <div className="flex">
            {/* Left panel - Mode selection */}
            <div className="w-28 border-r border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] rounded-l-lg">
              <div className="p-1.5 space-y-0.5">
                <button
                  type="button"
                  onClick={() => setCustomMode('fixed')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'fixed' ? MODE_ACTIVE_COLOR : 'var(--text-primary)',
                    fontWeight: customMode === 'fixed' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Fixed
                </button>
                <button
                  type="button"
                  onClick={() => setCustomMode('last')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'last' ? MODE_ACTIVE_COLOR : 'var(--text-primary)',
                    fontWeight: customMode === 'last' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Last
                </button>
                <button
                  type="button"
                  onClick={() => setCustomMode('since')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'since' ? MODE_ACTIVE_COLOR : 'var(--text-primary)',
                    fontWeight: customMode === 'since' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Since
                </button>
              </div>
            </div>

            {/* Right panel - Configuration */}
            <div className="flex-1 p-3">
              {/* Fixed mode */}
              {customMode === 'fixed' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="text-xs text-[color:var(--text-muted)] mb-1">Starts</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-1.5 bg-surface-hover rounded-lg text-sm text-[color:var(--text-primary)]">
                          {startDate || 'Select start date'}
                        </div>
                        {enableTimeRanges && (
                          <input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            className="px-2 py-1.5 bg-surface-hover border border-[color:var(--border-default)] rounded-lg text-sm text-[color:var(--text-primary)] w-24"
                          />
                        )}
                      </div>
                    </div>
                    <div className="text-[color:var(--text-muted)] text-lg pt-5">→</div>
                    <div className="flex-1">
                      <div className="text-xs text-[color:var(--text-muted)] mb-1">Ends</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-1.5 bg-surface-hover rounded-lg text-sm text-[color:var(--text-primary)]">
                          {endDate || 'Select end date'}
                        </div>
                        {enableTimeRanges && (
                          <input
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            className="px-2 py-1.5 bg-surface-hover border border-[color:var(--border-default)] rounded-lg text-sm text-[color:var(--text-primary)] w-24"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inline calendar (range) - 2 months side by side */}
                  <div className="pt-1">
                    <div className="grid grid-cols-2 gap-3">
                      <CalendarGrid mode="fixed" month={calendarMonth1} onMonthChange={setCalendarMonth1} />
                      <CalendarGrid mode="fixed" month={calendarMonth2} onMonthChange={setCalendarMonth2} />
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] mt-1.5">
                      Click a start date, then an end date.
                    </div>
                  </div>
                </div>
              )}

              {/* Last mode */}
              {customMode === 'last' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[color:var(--text-muted)] mb-1.5">
                      Show me last
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={lastAmount}
                        onChange={(e) => setLastAmount(parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-20 px-2.5 py-1.5 border border-[color:var(--border-default)] rounded-lg text-sm"
                      />
                      <select
                        value={lastUnit}
                        onChange={(e) => {
                          setLastUnit(e.target.value);
                          setEndingUnit(e.target.value); // Keep units in sync
                        }}
                        className="px-2.5 py-1.5 border border-[color:var(--border-default)] rounded-lg text-sm"
                      >
                        {timeUnits.map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[color:var(--text-muted)] mb-1.5">
                      Ending
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={endingAmount}
                        onChange={(e) => setEndingAmount(parseInt(e.target.value) || 0)}
                        min="0"
                        className="w-20 px-2.5 py-1.5 border border-[color:var(--border-default)] rounded-lg text-sm"
                      />
                      <select
                        value={endingUnit}
                        onChange={(e) => {
                          setEndingUnit(e.target.value);
                          setLastUnit(e.target.value); // Keep units in sync
                        }}
                        className="px-2.5 py-1.5 border border-[color:var(--border-default)] rounded-lg text-sm"
                      >
                        {timeUnits.map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-[color:var(--text-muted)]">ago</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Since mode */}
              {customMode === 'since' && (
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-1">From date</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-1.5 bg-surface-hover rounded-lg text-sm text-[color:var(--text-primary)]">
                        {sinceDate || 'Select start date'}
                      </div>
                      {enableTimeRanges && (
                        <input
                          type="time"
                          value={sinceTime}
                          onChange={(e) => setSinceTime(e.target.value)}
                          className="px-2 py-1.5 bg-surface-hover border border-[color:var(--border-default)] rounded-lg text-sm text-[color:var(--text-primary)] w-24"
                        />
                      )}
                    </div>
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">
                      End date: {new Date().toLocaleDateString()} (today)
                    </p>
                  </div>

                  {/* Inline calendar (single) - 2 months side by side */}
                  <div className="pt-1">
                    <div className="grid grid-cols-2 gap-3">
                      <CalendarGrid mode="since" month={calendarMonth1} onMonthChange={setCalendarMonth1} />
                      <CalendarGrid mode="since" month={calendarMonth2} onMonthChange={setCalendarMonth2} />
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons and Enable Time Ranges toggle */}
              <div className="mt-3 pt-3 border-t border-[color:var(--border-default)] flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setEnableTimeRanges(!enableTimeRanges)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-2 border border-[color:var(--border-default)] ${
                    enableTimeRanges
                      ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand'
                      : 'bg-surface text-[color:var(--text-muted)] hover:bg-[color:var(--surface-sunken)]'
                  }`}
                >
                  <span>Enable Time Ranges</span>
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsCustomOpen(false);
                      if (onClose) onClose();
                    }}
                    className="px-3 py-1.5 text-sm text-[color:var(--text-muted)] hover:bg-[color:var(--surface-sunken)] rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomApply}
                    disabled={
                      (customMode === 'fixed' && (!startDate || !endDate)) ||
                      (customMode === 'since' && !sinceDate) ||
                      (customMode === 'last' && (!lastAmount || lastAmount <= 0))
                    }
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors border border-[color:var(--border-default)] ${
                      (customMode === 'fixed' && (!startDate || !endDate)) ||
                      (customMode === 'since' && !sinceDate) ||
                      (customMode === 'last' && (!lastAmount || lastAmount <= 0))
                        ? 'bg-surface-hover text-[color:var(--text-muted)] cursor-not-allowed'
                        : 'bg-[color:var(--trodo-primary)] text-ink-onBrand hover:bg-[color:var(--trodo-primary-hover)]'
                    }`}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
      </FloatingMenu>
    </div>
  );
};

export default EventsTimeFilter;
